import type { StaticImageData } from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import cloudflareLoader from '@/utils/images/cloudflareLoader';
import BannersSkeleton from './BannersSkeleton';

// --- Types ---

export interface BetterPictureProps {
  src: string | StaticImageData;
  srcMobile?: string | StaticImageData;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  quality?: number;
  fetchPriority?: 'high' | 'auto';
  priority?: boolean;
}

// --- Helpers ---

const getImageSrc = (src: string | StaticImageData): string =>
  typeof src === 'string' ? src : src.src;

// --- Component ---

export const BetterPicture = ({
  src,
  srcMobile,
  alt,
  className,
  width,
  height,
  priority,
  fetchPriority = 'auto',
  quality = 100,
  ...props
}: BetterPictureProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  // Source strings & quality
  const srcString = getImageSrc(src);
  const srcMobileString = srcMobile ? getImageSrc(srcMobile) : undefined;
  const q = quality ?? 100;

  // CDN URLs (1x + 2x for responsive srcset)
  const desktopSrc1x = cloudflareLoader({ src: srcString, width: 1133, quality: q });
  const desktopSrc2x = cloudflareLoader({ src: srcString, width: 2266, quality: q });
  const mobileSrc1x = srcMobileString
    ? cloudflareLoader({ src: srcMobileString, width: 328, quality: q })
    : undefined;
  const mobileSrc2x = srcMobileString
    ? cloudflareLoader({ src: srcMobileString, width: 656, quality: q })
    : undefined;

  // Cached images may fire load before our onLoad is attached — check img.complete
  useEffect(() => {
    setIsLoading(true);
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setIsLoading(false);
    }
  }, [srcString]);

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {isLoading && (
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          <BannersSkeleton />
        </div>
      )}
      <picture className="block size-full backface-hidden">
        {srcMobileString && (
          <source
            media="(max-width: 39.9375rem)"
            srcSet={
              mobileSrc1x && mobileSrc2x
                ? `${mobileSrc1x} 241w, ${mobileSrc2x} 482w`
                : mobileSrc1x ?? ''
            }
            sizes="(min-width: 420px) calc(100vw - 32px), (min-width: 380px) 348px, (min-width: 340px) 308px, 268px"
          />
        )}

        <img
          ref={imgRef}
          {...props}
          src={desktopSrc1x}
          srcSet={`${desktopSrc1x} 1133w, ${desktopSrc2x} 2266w`}
          sizes="(min-width: 1520px) 1133px, (min-width: 1040px) calc(88.04vw - 188px), (min-width: 420px) calc(100vw - 32px), (min-width: 380px) 348px, (min-width: 340px) 308px, 268px"
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={fetchPriority}
          className={cn(
            'size-full object-cover duration-100 opacity-transition backface-hidden',
            isLoading ? 'opacity-0' : 'opacity-100',
          )}
          onLoad={() => setIsLoading(false)}
        />
      </picture>
    </div>
  );
};
